import React, { useState, useEffect, useRef } from 'react';
import CustomScrollTrigger from 'js/wrapper/CustomScrollTrigger';
import { Parallax } from 'react-scroll-parallax';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import SignupForm from 'components/forms/auth/SignupForm';
import RichEditorContent from 'components/RichEditorContent';
import { useAbstractCmsElement } from 'components/cms/hooks/useAbstractCmsElementNew';


function CmsElementSignupForm(props) {
  const { editMode, index, showElementSettings, intl } = props;

  // CMS Logic Hook
  const {
    shown,
    setShown,
    getMainId,
    getCustomId,
    getParamValue,
    getBackgroundStyle,
    elementStyleTag,
    getParallaxData,
    getAppendHtml,
    cssClasses,
    responsiveClasses,
  } = useAbstractCmsElement(props);

  // dnd-kit Sortable Hook
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: index,
    disabled: !editMode,
  });

  // Local State
  const [pending, setPending] = useState(false);
  const timeoutRef = useRef(null);
  const messages = intl?.messages || {};

  // Cleanup Timeout on Unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // Dummy onSubmit handling
  const handleOnClick = (e) => {
    if (e && e.preventDefault) e.preventDefault();

    clearTimeout(timeoutRef.current);
    setPending(true);

    timeoutRef.current = setTimeout(() => {
      setPending(false);
    }, 1000);
  };

  const dndStyle = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
    position: 'relative',
    cursor: editMode ? 'move' : 'default',
    width: '100%',
  };

  // Visibility & Classes
  const isVisible = editMode || shown;
  const shownClass = isVisible ? 'elem-shown' : 'elem-hidden';
  const textClass = `text-${getParamValue('mainColor', 'master')}`;

  const innerContent = (
    <div
      id={getMainId()}
      className={`ms-signup-form-content ${shownClass} ${cssClasses} ${responsiveClasses}`.trim()}
      onClick={(e) => editMode && showElementSettings(e, index)}
      style={getBackgroundStyle()}
    >
      {elementStyleTag}

      <div className="ms-signup-form-container">
        <h4 className={`no-margin p-b-5 ${textClass}`}>
          <i className="fa fa-pencil-alt m-r-5" />
          {getParamValue('title', '')}
        </h4>

        {/* Note: changed className to class for raw HTML string compatibility */}
        <RichEditorContent content={`<p class="${textClass}">${getParamValue('content', '')}</p>`} />

        <SignupForm
          {...props}
          onSubmit={handleOnClick}
          buttonTitle={getParamValue('buttonTitle', messages.signup || 'Sign Up')}
          pending={pending}
        />
      </div>

      {getAppendHtml()}
    </div>
  );

  const parallaxData = getParallaxData();

  return (
    <div ref={setNodeRef} style={dndStyle} {...attributes} {...listeners}>
      <div>
        <CustomScrollTrigger className="cms-overflow-fix" onEnter={() => setShown(true)} {...getCustomId()}>
          {parallaxData ? (
            <Parallax {...parallaxData}>
              {innerContent}
            </Parallax>
          ) : (
            innerContent
          )}
        </CustomScrollTrigger>
      </div>
    </div>
  );
}

export default CmsElementSignupForm;
